import React, { useState, useEffect } from 'react';
import api from '../../../../config/api-config';
import { useAuth } from '../../../../context/AuthContext';
import './SettingBoardModal.css';
import '../ShareBoardModal/ShareBoardModal.css';

function SettingBoardModal({ boardId, onClose, onSuccess }) {
  const [gameDesignDoc, setGameDesignDoc] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Fetch the existing game design document when the component mounts
    const fetchGameDesignDoc = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const token = await user.getIdToken();
        const response = await api.get(
          `/api/boards/${boardId}/gameDesignDoc`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        setGameDesignDoc(response.data.gameDesignDoc || '');
      } catch (err) {
        console.error('Error fetching game design document:', err);
        
        if (err.response && err.response.data && err.response.data.message) {
          setError(err.response.data.message);
        } else {
          setError('Failed to fetch game design document. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchGameDesignDoc();
  }, [boardId, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      setError(null);
      
      const token = await user.getIdToken();
      await api.put(
        `/api/boards/${boardId}/gameDesignDoc`,
        { gameDesignDoc },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setSuccess(true);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error saving game design document:', err);
      
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to save game design document. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Board Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {success ? (
            <div className="success-message">
              <p>Game design document saved successfully!</p>
              <button className="primary-button" onClick={() => setSuccess(false)}>Continue Editing</button>
              <button className="secondary-button" onClick={onClose}>Close</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="gameDesignDoc">Game Design Document</label>
                <div className="textarea-container">
                  <textarea
                    id="gameDesignDoc"
                    value={gameDesignDoc}
                    onChange={(e) => setGameDesignDoc(e.target.value)}
                    placeholder="Enter your game design document here..."
                    disabled={isLoading}
                    spellCheck="false"
                    autoComplete="off"
                  />
                </div>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <div className="modal-actions">
                <button 
                  type="button" 
                  className="secondary-button" 
                  onClick={onClose}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingBoardModal;
